import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, RotateCcw, MessageCircle, Sparkles } from "lucide-react";
import { FadeUp } from "@/components/motion/Reveal";
import { openWhatsApp } from "@/lib/wa";

const QUESTIONS = [
  {
    key: "purpose",
    q: "Neye hazırlanıyorsunuz?",
    options: [
      "Günlük bakım",
      "Özel bir gün",
      "Gelin / nişan hazırlığı",
      "Görünümümü yenilemek istiyorum",
    ],
  },
  {
    key: "area",
    q: "Hangi alanla ilgileniyorsunuz?",
    options: ["Saç", "Cilt", "Kaş ve kirpik", "Tırnak", "Epilasyon", "Türban tasarım"],
  },
  {
    key: "timing",
    q: "Ne zaman gelmek istersiniz?",
    options: ["En kısa zamanda", "Bu hafta", "Bu ay", "Sadece bilgi almak istiyorum"],
  },
];

const RECS = {
  Saç: "Saç & Kuaför uygulamalarımız",
  Cilt: "Cilt & Güzellik bakımlarımız",
  "Kaş ve kirpik": "Kaş tasarımı, microblading ve ipek kirpik uygulamalarımız",
  Tırnak: "Protez tırnak, kalıcı oje ve manikür-pedikür uygulamalarımız",
  Epilasyon: "Lazer ve iğneli epilasyon uygulamalarımız",
  "Türban tasarım": "Gelin & Türban tasarım hizmetlerimiz",
};

export default function Recommender() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const done = step >= QUESTIONS.length;

  const choose = (key, value) => {
    setAnswers((a) => ({ ...a, [key]: value }));
    setStep((s) => s + 1);
  };

  const reset = () => {
    setAnswers({});
    setStep(0);
  };

  const rec = RECS[answers.area] || "size en uygun hizmet grubumuz";

  const sendWhatsApp = () =>
    openWhatsApp(
      `Merhaba Hülya Suna Beauty, Güzellik Rotanı Bul alanından geliyorum. Hazırlık amacım: ${answers.purpose}. İlgilendiğim alan: ${answers.area}. Zaman tercihim: ${answers.timing}. Uygun hizmet ve randevu hakkında bilgi alabilir miyim?`
    );

  return (
    <section
      id="rota"
      className="relative bg-espresso text-ivory py-20 sm:py-28 overflow-hidden grain"
      data-testid="recommender-section"
    >
      <div className="relative z-10 mx-auto max-w-3xl px-5 sm:px-8 text-center">
        <FadeUp>
          <p className="eyebrow text-gold mb-4 flex items-center justify-center gap-2">
            <Sparkles size={14} /> İnteraktif Rehber
          </p>
        </FadeUp>
        <FadeUp delay={0.05}>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mb-4">
            Güzellik <span className="italic text-gold">Rotanı Bul</span>
          </h2>
        </FadeUp>
        <FadeUp delay={0.1}>
          <p className="text-ivory/70 max-w-xl mx-auto mb-10">
            Ne istediğinizden emin değil misiniz? Birkaç seçimle size uygun hizmet
            grubunu birlikte bulalım.
          </p>
        </FadeUp>

        {/* progress */}
        <div className="flex items-center justify-center gap-2 mb-10" data-testid="recommender-progress">
          {QUESTIONS.map((_, i) => (
            <span
              key={i}
              className={`h-1 rounded-full transition-all duration-500 ${
                (done ? QUESTIONS.length : step) > i ? "w-10 bg-gold" : "w-6 bg-ivory/20"
              }`}
            />
          ))}
        </div>

        <div className="min-h-[320px] flex items-center justify-center">
          <AnimatePresence mode="wait">
            {!done ? (
              <motion.div
                key={step}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -24 }}
                transition={{ duration: 0.45, ease: [0.19, 1, 0.22, 1] }}
                className="w-full"
                data-testid={`recommender-step-${step}`}
              >
                <p className="eyebrow text-gold/80 mb-3">
                  {String(step + 1).padStart(2, "0")} / 0{QUESTIONS.length}
                </p>
                <h3 className="font-serif text-3xl sm:text-4xl mb-8">{QUESTIONS[step].q}</h3>
                <div className="flex flex-wrap justify-center gap-3">
                  {QUESTIONS[step].options.map((opt) => (
                    <button
                      key={opt}
                      onClick={() => choose(QUESTIONS[step].key, opt)}
                      data-testid={`recommender-option-${QUESTIONS[step].key}-${opt}`}
                      className="rounded-full border border-ivory/25 px-6 py-3 text-sm text-ivory/90 transition-all duration-300 hover:bg-gold hover:border-gold hover:text-espresso hover:scale-[1.03]"
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="result"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease: [0.19, 1, 0.22, 1] }}
                className="w-full"
                data-testid="recommender-result"
              >
                <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full border border-gold text-gold">
                  <Check size={26} strokeWidth={1.6} />
                </div>
                <h3 className="font-serif text-3xl sm:text-4xl mb-4">Rotanız hazır</h3>
                <p className="text-ivory/80 max-w-lg mx-auto leading-relaxed mb-8">
                  Seçimlerinize göre, <span className="text-gold">{rec}</span> ile
                  başlamanızı öneririz. Kesin plan ve size en uygun uygulama için
                  uzmanımıza danışmanız yeterli.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <button
                    onClick={sendWhatsApp}
                    data-testid="recommender-whatsapp-cta"
                    className="inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-espresso text-sm font-semibold transition-transform duration-300 hover:scale-[1.03] active:scale-95"
                  >
                    <MessageCircle size={18} /> Uzmanımıza Danışın
                  </button>
                  <button
                    onClick={reset}
                    data-testid="recommender-reset"
                    className="inline-flex items-center gap-2 text-sm text-ivory/70 hover:text-ivory link-underline"
                  >
                    <RotateCcw size={15} /> Baştan Başla
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
